import { useState } from 'react';
import { Button } from '../common/Button';
import type { UserInfo } from '../../types';

interface EditUserModalProps {
  user: UserInfo;
  currentUserId: string;
  onClose: () => void;
  onSave: (userId: string, role: string) => Promise<unknown>;
  isLoading: boolean;
}

const roleOptions = [
  { value: 'basic-user', label: 'User' },
  { value: 'reviewer', label: 'Reviewer' },
  { value: 'framework-manager', label: 'Framework Manager' },
  { value: 'admin', label: 'Admin' },
];

const statusColors: Record<UserInfo['status'], string> = {
  active: 'bg-green-500/20 text-green-400',
  deactivated: 'bg-red-500/20 text-red-400',
};

export function EditUserModal({ user, currentUserId, onClose, onSave, isLoading }: EditUserModalProps) {
  // Parent mounts this modal fresh per open (keyed by user email), so
  // seeding state from props is safe — no reset-on-prop-change needed.
  const [selectedRole, setSelectedRole] = useState<string>(user.role);
  const [error, setError] = useState('');

  const isOwnUser = user.email === currentUserId;
  const hasChanged = selectedRole !== user.role;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await onSave(user.email, selectedRole);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-wolf-800 rounded-lg p-6 w-full max-w-md border border-wolf-700">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-white">Edit User</h2>
          <p className="text-sm text-wolf-400">{user.email}</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Read-only user details */}
          <div className="space-y-3 mb-6">
            <div>
              <label className="block text-xs text-wolf-500 mb-1">Name</label>
              <p className="text-white">{user.name}</p>
            </div>
            <div>
              <label className="block text-xs text-wolf-500 mb-1">Email</label>
              <p className="text-white">{user.email}</p>
            </div>
            <div>
              <label className="block text-xs text-wolf-500 mb-1">Status</label>
              <span className={`px-2 py-1 rounded text-xs ${statusColors[user.status]}`}>
                {user.status}
              </span>
            </div>
            <div>
              <label className="block text-xs text-wolf-500 mb-1">Created</label>
              <p className="text-wolf-300">{new Date(user.created_at).toLocaleDateString()}</p>
            </div>
          </div>

          {/* Editable role */}
          <div className="mb-4">
            <label htmlFor="role" className="block text-sm text-wolf-300 mb-2">
              Role
            </label>
            <select
              id="role"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              disabled={isOwnUser || isLoading}
              className="w-full px-3 py-2 bg-wolf-900 border border-wolf-600 rounded text-white focus:outline-none focus:border-action disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {roleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {isOwnUser && (
              <p className="text-xs text-wolf-500 mt-1">You cannot change your own role</p>
            )}
          </div>

          {error && (
            <p className="text-red-400 text-sm mb-4">{error}</p>
          )}

          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || isOwnUser || !hasChanged}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
